import { useState, useCallback } from 'react'
import { OrgOption } from '../types'
import { mapOptionsToShareTargets, isAncestor, isDescendant } from '../utils'

export function useOrganizationSelection(
  onChange?: (value: { id: string; type: string }[]) => void
) {
  const [selectedOptions, setSelectedOptions] = useState<OrgOption[]>([])

  const handleSelectionChange = useCallback(
    (selected: OrgOption[]) => {
      onChange?.(mapOptionsToShareTargets(selected))
      setSelectedOptions(selected)
    },
    [onChange]
  )

  const handleOptionToggle = useCallback(
    (option: OrgOption | OrgOption[]) => {
      let selections: OrgOption[] = []

      if (Array.isArray(option)) {
        selections = option
      } else {
        const existingIndex = selectedOptions.findIndex(
          (o) => o.value.id === option.value.id
        )

        if (existingIndex > -1) {
          selections = selectedOptions.filter(
            (o) => o.value.id !== option.value.id
          )
        } else {
          // Implementing hierarchical permission inheritance
          selections = [
            option,
            ...selectedOptions.filter((existing) => {
              // Remove ancestors (parent already selected makes child redundant)
              // Remove descendants (child already selected makes parent redundant)
              const shouldRemove =
                isAncestor(existing, option) || isDescendant(existing, option)

              return !shouldRemove
            })
          ]
        }
      }

      handleSelectionChange(selections)
    },
    [selectedOptions, handleSelectionChange]
  )

  const isOptionSelected = useCallback(
    (option: OrgOption) =>
      selectedOptions.some((s) => s.value.id === option.value.id),
    [selectedOptions]
  )

  return {
    selectedOptions,
    handleSelectionChange,
    handleOptionToggle,
    isOptionSelected
  }
}
